import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { Link } from 'react-router-dom';
import { ContentBox } from '../../../../shared/content-box/ContentBox';
import { SpinLoader } from '../../../../shared/spin-loader/SpinLoader';
import { LabeledSelect } from '../../../../shared/labeled-select/LabeledSelect';
import TransparentButton from '../../../../shared/button/transparent-button/TransparentButton';
import { BlueContainedButton } from '../../../../shared/button/blue-contained-button/BlueContainedButton';
import { ScenarioPicker } from '../runs/ScenarioPicker';
import { LimitsBanner } from '../runs/LimitsBanner';
import { SandboxRunsNote } from '../runs/SandboxRunsNote';
import { makeWorkspaceOptions } from '../runs/NewRunContainer';
import { RuleChangeEditor } from './RuleChangeEditor';
import { RiskRatioEditor } from './RiskRatioEditor';
import { fetchScenarios, fetchSimulationLimits } from '../redux/runs/actions';
import { fetchWorkspaces } from '../redux/workspaces/actions';
import {
    fetchWorkspaceConfig,
    startComparison
} from '../redux/comparison/actions';
import {
    getComparisonState,
    getLimitsState,
    getScenariosState,
    getSimulationState,
    getWorkspacesState
} from '../redux/selectors';
import { redirectTo } from '../../../../routing/redirectService';
import {
    diffRiskRatios,
    diffRuleChanges,
    getDefaultParameterValues,
    hasInvalidRatio,
    isValidSeed,
    toScenarioRequests,
    validateParameterValue
} from '../simulation.model';
import { SimulationPath } from '../simulation.paths';

/*
  "Test this change": try a change to the rules, and to the ratios of the risk factors, of a sandbox. The same players are played
  twice, as the rules are and with the change, and the two are compared.
*/
class TestChangeContainer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            workspace: null,
            seed: '',
            label: '',
            // The values of the parameters of the scenarios that are chosen, by the id of the scenario
            selected: {},
            // What is typed in for the rules, by rule type: { config: { variable: value }, enabled }
            ruleEdits: {},
            // What is typed in for the ratios, by category and then by the key of the risk factor
            ratioEdits: {}
        };
    }

    componentDidMount() {
        this.props.fetchWorkspaces();
        this.props.fetchScenarios();
        this.props.fetchSimulationLimits();
    }

    scenariosById = () => _.keyBy(this.props.scenarios, 'id');

    chooseWorkspace = workspace =>
        // The rules and the ratios of another sandbox are other ones, so what was typed in for this one goes
        this.setState({ workspace, ruleEdits: {}, ratioEdits: {} }, () =>
            this.props.fetchWorkspaceConfig({ workspace })
        );

    toggleScenario = id =>
        this.setState(({ selected }) => ({
            selected: _.has(selected, id)
                ? _.omit(selected, id)
                : {
                      ...selected,
                      [id]: getDefaultParameterValues(this.scenariosById()[id])
                  }
        }));

    changeScenarioValue = (id, key, value) =>
        this.setState(({ selected }) => ({
            selected: { ...selected, [id]: { ...selected[id], [key]: value } }
        }));

    changeVariable = (ruleType, name, value) =>
        this.setState(({ ruleEdits }) => ({
            ruleEdits: {
                ...ruleEdits,
                [ruleType]: {
                    ...ruleEdits[ruleType],
                    config: {
                        ..._.get(ruleEdits, [ruleType, 'config']),
                        [name]: value
                    }
                }
            }
        }));

    changeEnabled = (ruleType, enabled) =>
        this.setState(({ ruleEdits }) => ({
            ruleEdits: {
                ...ruleEdits,
                [ruleType]: { ...ruleEdits[ruleType], enabled }
            }
        }));

    resetRule = ruleType =>
        this.setState(({ ruleEdits }) => ({
            ruleEdits: _.omit(ruleEdits, ruleType)
        }));

    changeRatio = (category, key, value) =>
        this.setState(({ ratioEdits }) => ({
            ratioEdits: {
                ...ratioEdits,
                [category]: { ..._.get(ratioEdits, category), [key]: value }
            }
        }));

    chosenScenarios = () =>
        _.filter(this.props.scenarios, s => _.has(this.state.selected, s.id));

    hasInvalidParameter = () =>
        _.some(this.chosenScenarios(), scenario =>
            _.some(scenario.parameters, parameter =>
                validateParameterValue(
                    parameter,
                    this.state.selected[scenario.id][parameter.key]
                )
            )
        );

    ruleChanges = () => diffRuleChanges(this.props.rules, this.state.ruleEdits);

    riskRatios = () => diffRiskRatios(this.props.risk, this.state.ratioEdits);

    countChanges = () =>
        this.ruleChanges().length +
        _.sum(_.map(this.riskRatios(), ratios => _.size(ratios)));

    canStart = () => {
        const { limits, submitting, configLoaded } = this.props;
        const { workspace, seed, ratioEdits } = this.state;

        return (
            !!workspace &&
            configLoaded &&
            !submitting &&
            !_.isEmpty(this.chosenScenarios()) &&
            !this.hasInvalidParameter() &&
            !hasInvalidRatio(ratioEdits) &&
            isValidSeed(seed) &&
            this.countChanges() > 0 &&
            (!limits || limits.enabled)
        );
    };

    start = () => {
        const { workspace, seed, label } = this.state;
        const chosen = this.chosenScenarios().map(scenario => ({
            id: scenario.id,
            values: this.state.selected[scenario.id]
        }));
        const riskRatios = this.riskRatios();

        this.props.startComparison({
            workspace,
            seed: seed === '' ? undefined : seed,
            label: _.trim(label) || undefined,
            scenarios: toScenarioRequests(chosen, this.scenariosById()),
            changes: this.ruleChanges(),
            riskRatios: _.isEmpty(riskRatios) ? undefined : riskRatios
        });
    };

    renderWorkspace() {
        const { workspaces, workspacesLoaded } = this.props;

        if (!workspacesLoaded) {
            return <SpinLoader />;
        }
        if (_.isEmpty(workspaces)) {
            return (
                <div className={'sim-banner warning'}>
                    <i className={'material-icons'}>warning</i>
                    <div>
                        There is no sandbox to test in yet.{' '}
                        <Link to={SimulationPath.WORKSPACES}>
                            Create a sandbox
                        </Link>{' '}
                        first.
                    </div>
                </div>
            );
        }
        return (
            <div>
                <LabeledSelect
                    className={'sim-field'}
                    label={'Sandbox to test in'}
                    options={makeWorkspaceOptions(workspaces)}
                    value={this.state.workspace}
                    onChange={this.chooseWorkspace}
                />
                <SandboxRunsNote
                    limits={this.props.limits}
                    workspace={_.find(workspaces, {
                        code: this.state.workspace
                    })}
                    places={2}
                    onOpenRuns={() => redirectTo(SimulationPath.RUNS)}
                />
            </div>
        );
    }

    renderChanges() {
        const { workspace, ruleEdits } = this.state;
        const { configLoaded, rules } = this.props;

        if (!workspace) {
            return (
                <span className={'sim-muted'}>
                    Choose a sandbox to see its rules.
                </span>
            );
        }
        if (!configLoaded) {
            return <SpinLoader />;
        }
        return (
            <RuleChangeEditor
                rules={rules}
                edits={ruleEdits}
                onChangeVariable={this.changeVariable}
                onChangeEnabled={this.changeEnabled}
                onReset={this.resetRule}
            />
        );
    }

    render() {
        const { limits, configLoaded, risk, refusal } = this.props;
        const { workspace, seed, label, ratioEdits } = this.state;
        const changes = this.countChanges();

        return (
            <div className={'sim-test-change sim-form'}>
                <div className={'sim-section-note'}>
                    Try a change to the rules or to the risk ratios of a sandbox
                    without touching them. The same players are played twice,
                    with the rules as they are and with the change, one run
                    after the other, and then compared.
                </div>
                <LimitsBanner
                    limits={limits}
                    blockingRunId={refusal && refusal.activeRunId}
                    onOpenRun={runId => redirectTo(SimulationPath.run(runId))}
                />

                <div className={'sim-section'}>
                    <ContentBox
                        title={'Where'}
                        content={this.renderWorkspace()}
                    />
                </div>
                <div className={'sim-section'}>
                    <ContentBox
                        title={'What to change'}
                        subtitle={`${changes || 'No'} ${
                            changes === 1 ? 'change' : 'changes'
                        }`}
                        content={this.renderChanges()}
                    />
                </div>
                {workspace && configLoaded && (
                    <div className={'sim-section'}>
                        <ContentBox
                            title={'Risk ratios'}
                            tooltipMessage={
                                'The weight of each risk factor follows from its ratio: its share of the total of the category.'
                            }
                            content={
                                <RiskRatioEditor
                                    risk={risk}
                                    edits={ratioEdits}
                                    onChange={this.changeRatio}
                                />
                            }
                        />
                    </div>
                )}
                <div className={'sim-section'}>
                    <ContentBox
                        title={'Players to play'}
                        subtitle={`${_.size(this.state.selected) ||
                            'None'} chosen`}
                        content={
                            this.props.scenariosLoaded ? (
                                <ScenarioPicker
                                    scenarios={this.props.scenarios}
                                    selected={this.state.selected}
                                    onToggle={this.toggleScenario}
                                    onChangeValue={this.changeScenarioValue}
                                />
                            ) : (
                                <SpinLoader />
                            )
                        }
                    />
                </div>
                <div className={'sim-section'}>
                    <ContentBox
                        title={'Options'}
                        content={
                            <div className={'sim-field-row'}>
                                <div className={'sim-field'}>
                                    <span className={'input-label'}>Label</span>
                                    <input
                                        type={'text'}
                                        value={label}
                                        placeholder={'Optional'}
                                        onChange={event =>
                                            this.setState({
                                                label: event.target.value
                                            })
                                        }
                                    />
                                </div>
                                <div className={'sim-field'}>
                                    <span className={'input-label'}>Seed</span>
                                    <input
                                        type={'text'}
                                        className={
                                            isValidSeed(seed) ? '' : 'invalid'
                                        }
                                        value={seed}
                                        placeholder={'Picked for you'}
                                        onChange={event =>
                                            this.setState({
                                                seed: event.target.value
                                            })
                                        }
                                    />
                                    {isValidSeed(seed) ? (
                                        <span className={'sim-hint'}>
                                            Both runs are played with the same
                                            seed.
                                        </span>
                                    ) : (
                                        <span className={'sim-error'}>
                                            A seed is a whole number.
                                        </span>
                                    )}
                                </div>
                            </div>
                        }
                    />
                </div>

                <div className={'sim-actions-row'}>
                    <TransparentButton
                        onClick={() => redirectTo(SimulationPath.RUNS)}
                    >
                        Cancel
                    </TransparentButton>
                    <BlueContainedButton
                        text={
                            this.props.submitting
                                ? 'Starting…'
                                : 'Run baseline and variant'
                        }
                        disabled={!this.canStart()}
                        onClick={this.start}
                    />
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const workspaces = getWorkspacesState(state);
    const scenarios = getScenariosState(state);
    const config = getSimulationState(state).workspaceConfig;

    return {
        workspaces: workspaces.data,
        workspacesLoaded: workspaces.loaded,
        scenarios: scenarios.data,
        scenariosLoaded: scenarios.loaded,
        limits: getLimitsState(state).data,
        rules: config.rules,
        risk: config.risk,
        configLoaded: config.loaded,
        submitting: getComparisonState(state).submitting,
        refusal: getComparisonState(state).refusal
    };
};

const mapDispatchToProps = dispatch => ({
    fetchWorkspaces: () => dispatch(fetchWorkspaces()),
    fetchScenarios: () => dispatch(fetchScenarios()),
    fetchSimulationLimits: () => dispatch(fetchSimulationLimits()),
    fetchWorkspaceConfig: args => dispatch(fetchWorkspaceConfig(args)),
    startComparison: args => dispatch(startComparison(args))
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TestChangeContainer);
